import { useState } from 'react';

import { ButtonBase } from '@mui/material';
import { Theme } from '@mui/material/styles';
import { GridSlotProps, useGridApiRef } from '@mui/x-data-grid-premium';

import { Box } from '../box/box';
import { useTheme } from '../styles/styles';
import {
  SortFilterGridFlags,
  SortFilterPopover,
  useGridSortFilterModels,
} from './sort-filter-popover';

type ApiRef = ReturnType<typeof useGridApiRef>;

export const cardIconButtonSx = (theme: Theme) => ({
  position: 'relative' as const,
  width: 32,
  height: 32,
  flexShrink: 0,
  borderRadius: '8px',
  border: `1px solid ${theme.custom?.controlBorder}`,
  background: theme.background.white,
  color: theme.custom?.brandIndigo,
  '&:hover': { background: theme.custom?.hoverBg },
});

const HeaderSortArrow = ({
  direction,
}: GridSlotProps['columnHeaderSortIcon']) => {
  const theme = useTheme();
  if (!direction) return null;
  return (
    <Box
      component="svg"
      viewBox="0 0 24 24"
      aria-hidden
      sx={{
        width: 13,
        height: 13,
        flex: 'none',
        marginLeft: '4px',
        fill: 'none',
        stroke: theme.colors.primary.main,
        strokeWidth: 2,
        strokeLinecap: 'round',
        strokeLinejoin: 'round',
      }}
    >
      <path
        d={
          direction === 'asc'
            ? 'M12 19V5M6 11l6-6 6 6'
            : 'M12 5v14M6 13l6 6 6-6'
        }
      />
    </Box>
  );
};

const HeaderFilterDot = ({
  counter,
}: GridSlotProps['columnHeaderFilterIconButton']) => {
  const theme = useTheme();
  if (!counter) return null;
  return (
    <Box
      component="span"
      title="Filtered"
      sx={{
        width: 6,
        height: 6,
        flex: 'none',
        order: 1,
        marginLeft: '5px',
        borderRadius: '50%',
        background: theme.colors.primary.main,
      }}
    />
  );
};

export const sortFilterHeaderSlots = {
  columnHeaderSortIcon: HeaderSortArrow,
  columnHeaderFilterIconButton: HeaderFilterDot,
};

export const SortFilterButton = ({
  apiRef,
  flags,
  sx,
}: {
  apiRef: ApiRef;
  flags: SortFilterGridFlags;
  sx?: Record<string, unknown>;
}) => {
  const theme = useTheme();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const { sortModel, active } = useGridSortFilterModels(apiRef);
  const count = sortModel.length + active;
  return (
    <>
      <ButtonBase
        aria-label={`Sort & filter${count ? ` (${count} active)` : ''}`}
        title="Sort & filter"
        aria-haspopup="dialog"
        aria-expanded={Boolean(anchor)}
        onClick={(e) => {
          e.stopPropagation();
          setAnchor(e.currentTarget);
        }}
        sx={{
          ...cardIconButtonSx(theme),
          ...(count > 0 && {
            color: theme.custom?.shellMenuItemCurrentText,
            borderColor: theme.custom?.copyChipOnBorder,
            background: theme.custom?.copyChipHoverBg,
          }),
          ...sx,
        }}
      >
        <Box
          component="svg"
          viewBox="0 0 24 24"
          aria-hidden
          sx={{
            width: 16,
            height: 16,
            fill: 'none',
            stroke: 'currentColor',
            strokeWidth: 1.8,
            strokeLinecap: 'round',
            strokeLinejoin: 'round',
          }}
        >
          <path d="M4 7h9M4 12h6M4 17h4" />
          <path d="M17 5v14M14 16l3 3 3-3" />
        </Box>
        {count > 0 && (
          <Box
            component="span"
            aria-hidden
            sx={{
              position: 'absolute',
              top: '-6px',
              right: '-6px',
              minWidth: 17,
              height: 17,
              padding: '0 4px',
              boxSizing: 'border-box',
              display: 'grid',
              placeItems: 'center',
              fontSize: '10.5px',
              fontWeight: 600,
              lineHeight: 1,
              color: theme.background.white,
              background: theme.colors.primary.main,
              borderRadius: '999px',
              boxShadow: `0 0 0 2px ${theme.background.white}`,
            }}
          >
            {count}
          </Box>
        )}
      </ButtonBase>
      <SortFilterPopover
        apiRef={apiRef}
        flags={flags}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
      />
    </>
  );
};
